import React, { useRef, useState, useEffect } from 'react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '../lib/googleMapsLoader';

const LocationAutocomplete = ({
  value,
  onChange,
  onPlaceSelected,
  placeholder = 'Ciudad, dirección...',
  className = '',
  wrapperClassName = '',
  testId = 'location-autocomplete',
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const acRef = useRef(null);
  const [internalValue, setInternalValue] = useState(value || '');

  useEffect(() => { setInternalValue(value || ''); }, [value]);

  const handleLoad = (ac) => { acRef.current = ac; };

  const handlePlaceChanged = () => {
    if (!acRef.current) return;
    const place = acRef.current.getPlace();
    if (!place) return;
    const address = place.formatted_address || place.name || '';
    setInternalValue(address);
    onChange?.(address);
    if (onPlaceSelected && place.geometry?.location) {
      onPlaceSelected({
        address,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        place_id: place.place_id,
      });
    }
  };

  // Render the plain input (always enabled, always typeable). This is the SAFE base:
  // even if Google Maps fails to load (bad API key, referrer restriction, network),
  // the user can still type the address manually and the form submits fine.
  const plainInput = (
    <div className={`relative ${wrapperClassName}`}>
      <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10 pointer-events-none" />
      <Input
        value={internalValue}
        onChange={(e) => { setInternalValue(e.target.value); onChange?.(e.target.value); }}
        placeholder={placeholder}
        className={`pl-9 ${className}`}
        data-testid={testId}
      />
    </div>
  );

  // If no API key OR Google failed to load OR still loading → use the plain input.
  // The Autocomplete enhancement only kicks in once Google Maps is fully ready.
  if (!apiKey || loadError || !isLoaded) {
    return plainInput;
  }

  // Google Maps ready — wrap the plain input with Autocomplete for suggestions.
  return (
    <div className={`relative ${wrapperClassName}`}>
      <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10 pointer-events-none" />
      <Autocomplete
        onLoad={handleLoad}
        onPlaceChanged={handlePlaceChanged}
        options={{ types: ['geocode'], componentRestrictions: { country: 'mx' } }}
      >
        <Input
          value={internalValue}
          onChange={(e) => { setInternalValue(e.target.value); onChange?.(e.target.value); }}
          placeholder={placeholder}
          className={`pl-9 ${className}`}
          data-testid={testId}
        />
      </Autocomplete>
    </div>
  );
};

export default LocationAutocomplete;
